import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { filter, Subject, finalize, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgentApiService, type AgentRecord, type AgentPdSession } from 'src/app/core/services/agent-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import {
  CREATE_AGENT_STEPS,
  navigateCreateAgentStep,
} from '../create-agent/create-agent.paths';
import { isAgentUuid } from '../agent-route.util';
import { clientDetailPath } from '../../manage-client/client-route.util';
import {
  ConfirmActionDialogComponent,
  type ConfirmActionDialogData,
} from '../../manage-client/confirm-action-dialog.component';
import {
  agentLifecycleAction,
  agentStatusDisplayLabel,
  lifecycleConfirmCopy,
  lifecycleMenuLabel,
  statusBadgeClassForAgent,
} from '../agent-lifecycle.util';
import {
  isSuperAdminSimulatePdSession,
  pdSessionListDisplayCode,
} from 'src/app/shared/utils/pd-session-list.util';

@Component({
  selector: 'app-agent-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SharedModule, MaterialModule, MatDialogModule, IconModule, AppDatePipe],
  templateUrl: './agent-detail.component.html',
  styleUrl: './agent-detail.component.css',
})
export class AgentDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly agentApi = inject(AgentApiService);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  agentUuid = '';
  loading = true;
  loadError = '';
  agent: AgentRecord | null = null;
  toggleLoading = false;

  sessions: AgentPdSession[] = [];
  sessionsTotal = 0;
  sessionsPage = 1;
  sessionsPageSize = 10;
  sessionsLoading = false;
  sessionsStatusFilter = '';
  sessionsResultFilter = '';

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const uuid = (params.get('uuid') ?? '').trim();
      if (!isAgentUuid(uuid)) {
        this.loadError = 'Invalid agent link.';
        this.loading = false;
        return;
      }
      this.agentUuid = uuid;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.agent = null;

    this.agentApi
      .getAgentById(this.agentUuid)
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          if (!env.data) {
            this.loadError = 'Agent not found.';
            this.loading = false;
            return;
          }
          this.agent = env.data;
          this.loading = false;
          this.loadSessions();
        },
        error: (err) => {
          this.loading = false;
          this.loadError = resolveApiErrorMessage(err) || 'Could not load agent details.';
        },
      });
  }

  /** Opens the create-agent wizard at agent details (training entry point from view details). */
  retrainLink(): { commands: string[]; queryParams: Record<string, string> } | null {
    if (!this.agent?.uuid) return null;
    const nav = navigateCreateAgentStep(0, this.agent.uuid);
    return {
      commands: nav.commands,
      queryParams: (nav.extras.queryParams as Record<string, string>) ?? {},
    };
  }

  clientDetailLink(): string[] | null {
    const tenant = this.agent?.tenant;
    if (!tenant?.uuid) return null;
    return clientDetailPath({ uuid: tenant.uuid });
  }

  listTab(): 'published' | 'draft' {
    return this.agent?.status === 'DRAFT' ? 'draft' : 'published';
  }

  backToListLink(): string[] {
    return ['/manage-agent'];
  }

  statusBadgeClass(status: string | undefined): Record<string, boolean> {
    return statusBadgeClassForAgent(status);
  }

  statusDisplayLabel(status: string | undefined): string {
    return agentStatusDisplayLabel(status);
  }

canToggleStatus(): boolean {
    return agentLifecycleAction(this.agent?.status) != null;
  }

  toggleStatusLabel(): string {
    const label = lifecycleMenuLabel(this.agent?.status);
    return label ? `${label} agent` : '';
  }

  onToggleStatus(): void {
    if (!this.agent?.uuid || !this.canToggleStatus()) return;
    const agentLabel = this.agent.agent_code ?? this.agent.name ?? 'this agent';
    const copy = lifecycleConfirmCopy(this.agent.status, agentLabel);
    if (!copy) return;

    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      data: {
        title: copy.title,
        message: copy.message,
        confirmLabel: copy.confirmLabel,
        confirmColor: copy.confirmColor,
      } satisfies ConfirmActionDialogData,
      disableClose: true,
    });

    ref
      .afterClosed()
      .pipe(
        filter((ok) => ok === true),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.submitToggleStatus());
  }

  private submitToggleStatus(): void {
    if (!this.agent?.uuid) return;
    this.toggleLoading = true;
    this.agentApi
      .toggleStatus(this.agent.uuid)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.toggleLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (env) => {
          if (env.data) {
            this.agent = env.data;
          } else {
            this.load();
          }
        },
        error: () => {
          this.loadError = 'Could not update agent status.';
        },
      });
  }

  loadSessions(): void {
    if (!this.agentUuid) return;
    this.sessionsLoading = true;
    this.agentApi
      .getAgentSessions(this.agentUuid, {
        page: this.sessionsPage,
        page_size: this.sessionsPageSize,
        status: this.sessionsStatusFilter || undefined,
        result: this.sessionsResultFilter || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (env: any) => {
          const d = env?.data;
          this.sessions = d?.items ?? [];
          this.sessionsTotal = d?.total ?? 0;
          this.sessionsLoading = false;
        },
        error: () => { this.sessionsLoading = false; },
      });
  }

  onSessionsPageChange(page: number): void {
    this.sessionsPage = page;
    this.loadSessions();
  }

  onSessionsFilterChange(): void {
    this.sessionsPage = 1;
    this.loadSessions();
  }

  readonly sessionListDisplayCode = pdSessionListDisplayCode;
  readonly canOpenSessionInAuditTrail = isSuperAdminSimulatePdSession;

  /** Open a session's details in the audit trail (simulate sessions only). */
  viewSession(s: AgentPdSession, event?: Event): void {
    if (!isSuperAdminSimulatePdSession(s)) return;
    event?.stopPropagation();
    void this.router.navigate(['/audit-trail'], {
      queryParams: {
        session: String(s.id),
        ...(s.tenant_id != null && s.tenant_id > 0 ? { tenant_id: String(s.tenant_id) } : {}),
        return: this.router.url,
      },
    });
  }

  sessionsTotalPages(): number {
    return Math.ceil(this.sessionsTotal / this.sessionsPageSize);
  }

  sessionResultClass(result: string | null): string {
    if (result === 'PASS') return 'text-success font-semibold';
    if (result === 'FAIL') return 'text-danger font-semibold';
    if (result === 'REVIEW') return 'text-warning font-semibold';
    return 'text-gray-400';
  }

  sessionStatusClass(status: string): string {
    if (status === 'COMPLETED') return 'badge bg-success/20 text-success';
    if (status === 'IN_PROGRESS') return 'badge bg-info/20 text-info';
    if (status === 'FAILED') return 'badge bg-danger/20 text-danger';
    return 'badge bg-gray-100 text-gray-500 dark:bg-white/10';
  }

  wizardStepLabel(): string {
    const step = this.agent?.wizard_step ?? 1;
    const seg = CREATE_AGENT_STEPS[step - 1];
    if (!seg) return `Step ${step}`;
    return seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
}
